import * as yup from "yup";
import { LeasingType, LeasingTypeStructure } from "@interfaces/catalogs";
import {
  LeasingAssetDetails,
  LeasingPost,
  LeasingStructure,
  LeasingVehicleDetailsPost,
} from "@interfaces/LeasingTypes";
import { AssetForm } from "@interfaces/seleccionActivo";
import { FAMILIAS_ACTIVO_MOCK, TipoActivoMock } from "@pages/producto/credito_simple/tofu_v2/seleccionActivoMock";
import { formatCurrencyInput } from "./funciones";
import { cleanAmount } from "./informacionMultiGarantiasHelpers";

/** Funciones puras de `SeleccionActivo` (Pymio, flujo activo). */

export type AssetDetails = LeasingVehicleDetailsPost | LeasingAssetDetails;

export const CONDITIONS = [
  { value: "nuevo", label: "Nuevo" },
  { value: "seminuevo", label: "Seminuevo" },
];

/** `vehicle_type` de autos y SUVs: el mismo del arrendamiento original (`FormSeleccionVehiculos`). */
export const VEHICLE_TYPE_AUTOS = "Autos o Suvs";

/** Tipo de la familia vehicular que se elige del Libro Azul. */
export const AUTO_TYPE = "auto";

const conditionLabel = (value: string) => CONDITIONS.find((item) => item.value === value)?.label ?? value;
const conditionValue = (label: string | null | undefined) =>
  CONDITIONS.find((item) => item.label === label)?.value ?? "nuevo";

export const EMPTY_ASSET_FORM: AssetForm = {
  family: "",
  type: "",
  year: "",
  brand: "",
  model: "",
  version: "",
  description: "",
  amount: "",
  condition: "nuevo",
};

/** Ícono y tipos de una familia del mock, por el código del leasing type. */
export const mockFamilyOf = (code: string | undefined) => FAMILIAS_ACTIVO_MOCK.find((item) => item.code === code);

/**
 * Leasing types del tax system: solo las familias del prototipo (las del mock), activos, sin duplicados (el servicio
 * repite algunos) y en su orden.
 */
export const toLeasingTypes = (items: LeasingTypeStructure[]): LeasingType[] => {
  const byId = new Map<string, LeasingType>();
  for (const item of items) {
    if (
      item.active &&
      item.leasing_type?.active !== false &&
      mockFamilyOf(item.leasing_type?.code) &&
      !byId.has(item.leasing_type_id)
    ) {
      byId.set(item.leasing_type_id, item.leasing_type);
    }
  }
  return [...byId.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/**
 * Validación: el tipo si la familia tiene tipos; en vehículos año, marca, modelo y versión; en los demás activos la
 * descripción; siempre el valor. Depende de la familia elegida, así que recibe cómo resolverla.
 */
export const buildAssetSchema = (family: { isVehicle: (id: string) => boolean; hasTypes: (id: string) => boolean }) => {
  const requiredWhen = (when: (familyId: string) => boolean) =>
    yup.string().test("required", "Campo requerido", (value, ctx) => !when(ctx.parent.family) || !!value?.trim());

  return yup.object().shape({
    family: yup.string().required("Campo requerido"),
    type: requiredWhen(family.hasTypes),
    year: requiredWhen(family.isVehicle),
    brand: requiredWhen(family.isVehicle),
    model: requiredWhen(family.isVehicle),
    version: requiredWhen(family.isVehicle),
    description: requiredWhen((id) => !family.isVehicle(id)),
    amount: yup.string().required("Campo requerido"),
  });
};

/**
 * Payload del leasing. Vehículos con el `details` del arrendamiento original (en autos, `libroAzulNames` trae los
 * nombres de los códigos elegidos); los demás activos con `LeasingAssetDetails`.
 */
export const buildAssetLeasing = ({
  applicationId,
  form,
  type,
  isVehicle,
  libroAzulNames,
}: {
  applicationId: string;
  form: AssetForm;
  type: TipoActivoMock | undefined;
  isVehicle: boolean;
  libroAzulNames: { make: string; model: string; version: string };
}): LeasingPost<AssetDetails> => {
  const condition = conditionLabel(form.condition);
  const isAuto = isVehicle && form.type === AUTO_TYPE;

  const details: AssetDetails = isVehicle
    ? {
        vehicle_type: isAuto ? VEHICLE_TYPE_AUTOS : (type?.label ?? ""),
        year: Number(form.year),
        ...(isAuto
          ? libroAzulNames
          : { make: form.brand.trim(), model: form.model.trim(), version: form.version.trim() }),
        car_version_code: isAuto ? Number(form.version) || 0 : 0,
        condition,
        estimated_mileage: null,
      }
    : { asset_type: type?.label ?? "", description: form.description.trim(), condition };

  return { loan_application_id: applicationId, details, quantity: 1, estimated_value: cleanAmount(form.amount) };
};

/**
 * Lo guardado en el leasing, de vuelta al formulario. Los autos se regresan aparte (`autoDetails`) porque sus códigos
 * salen de volver a consultar el Libro Azul (`useLibroAzul.hydrate`).
 */
export const hydrateAsset = (
  base: AssetForm,
  leasing: LeasingStructure<AssetDetails>,
  types: TipoActivoMock[],
): { values: AssetForm; autoDetails?: LeasingVehicleDetailsPost } => {
  const values: AssetForm = {
    ...base,
    amount: leasing.estimated_value ? formatCurrencyInput(String(Math.round(leasing.estimated_value))) : "",
    condition: conditionValue(leasing.details?.condition),
  };
  const details = leasing.details;
  if (!details) return { values };

  const typeByLabel = (label: string) => types.find((item) => item.label === label)?.id ?? base.type;

  if (!("vehicle_type" in details)) {
    return { values: { ...values, type: typeByLabel(details.asset_type), description: details.description ?? "" } };
  }
  if (details.vehicle_type === VEHICLE_TYPE_AUTOS) {
    return { values: { ...values, type: AUTO_TYPE }, autoDetails: details };
  }
  return {
    values: {
      ...values,
      type: typeByLabel(details.vehicle_type),
      year: details.year ? String(details.year) : "",
      brand: details.make ?? "",
      model: details.model ?? "",
      version: details.version ?? "",
    },
  };
};
